import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Plus, CreditCard, Edit, Check, X, Infinity } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { applyCurrencyMask, parseCurrency } from '@/lib/input-masks';
// Interface para planos de assinatura
// IMPORTANTE: Usar stripe_price_id_monthly/yearly para consistência com useTenantBilling
interface SubscriptionPlan {
  id: string;
  name: string;
  description: string | null;
  max_inboxes: number;
  max_agents: number;
  max_channels: number;
  max_messages_month: number | null;
  // Limites do módulo de Tarefas (-1 = ilimitado)
  max_tasks_month: number;
  max_tasks_storage_mb: number;
  price_monthly: number;
  price_yearly: number;
  features: string[];
  stripe_product_id: string | null;
  stripe_price_id_monthly: string | null;
  stripe_price_id_yearly: string | null;
  is_active: boolean;
  sort_order: number;
}

type PlanFormData = Omit<SubscriptionPlan, 'features'> & { features: string };

const emptyPlan: PlanFormData = {
  id: '',
  name: '',
  description: '',
  max_inboxes: 1,
  max_agents: 3,
  max_channels: 1,
  max_messages_month: 1000,
  max_tasks_month: 50,
  max_tasks_storage_mb: 1024,
  price_monthly: 0,
  price_yearly: 0,
  features: '',
  stripe_product_id: null,
  stripe_price_id_monthly: null,
  stripe_price_id_yearly: null,
  is_active: true,
  sort_order: 0,
};

const formatLimit = (value: number | null) => {
  if (value === null || value === -1) return <Infinity className="h-4 w-4" />;
  return value.toLocaleString('pt-BR');
};

const formatPrice = (value: number) => {
  if (value === 0) return 'Grátis';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

const AdminPlans = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<PlanFormData | null>(null);
  const [formData, setFormData] = useState<PlanFormData>(emptyPlan);
  const { toast } = useToast();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;

      setPlans((data || []).map(p => ({
        ...p,
        features: Array.isArray(p.features) ? (p.features as string[]) : [],
        price_monthly: Number(p.price_monthly) || 0,
        price_yearly: Number(p.price_yearly) || 0,
      })));
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os planos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingPlan(null);
    setFormData(emptyPlan);
    setDialogOpen(true);
  };

  const openEditDialog = (plan: SubscriptionPlan) => {
    const formPlan: PlanFormData = {
      ...plan,
      features: plan.features.join('\n'),
    };
    setEditingPlan(formPlan);
    setFormData(formPlan);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.id || !formData.name) {
      toast({
        title: 'Erro',
        description: 'Preencha o ID e nome do plano',
        variant: 'destructive',
      });
      return;
    }

    try {
      const planData = {
        id: formData.id.toLowerCase().replace(/\s+/g, '_'),
        name: formData.name,
        description: formData.description || null,
        max_inboxes: formData.max_inboxes,
        max_agents: formData.max_agents,
        max_channels: formData.max_channels,
        max_messages_month: formData.max_messages_month,
        max_tasks_month: formData.max_tasks_month,
        max_tasks_storage_mb: formData.max_tasks_storage_mb,
        price_monthly: formData.price_monthly,
        price_yearly: formData.price_yearly,
        features: formData.features.split('\n').filter(f => f.trim()),
        stripe_product_id: formData.stripe_product_id || null,
        stripe_price_id_monthly: formData.stripe_price_id_monthly || null,
        stripe_price_id_yearly: formData.stripe_price_id_yearly || null,
        is_active: formData.is_active,
        sort_order: formData.sort_order,
      };

      if (editingPlan) {
        const { error } = await supabase
          .from('subscription_plans')
          .update(planData)
          .eq('id', editingPlan.id);

        if (error) throw error;

        toast({ title: 'Sucesso', description: 'Plano atualizado com sucesso' });
      } else {
        const { error } = await supabase
          .from('subscription_plans')
          .insert(planData);

        if (error) {
          if (error.message.includes('duplicate')) {
            toast({
              title: 'Erro',
              description: 'Já existe um plano com este ID',
              variant: 'destructive',
            });
            return;
          }
          throw error;
        }

        toast({ title: 'Sucesso', description: 'Plano criado com sucesso' });
      }

      setDialogOpen(false);
      fetchPlans();
    } catch (error) {
      console.error('Error saving plan:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível salvar o plano',
        variant: 'destructive',
      });
    }
  };

  const togglePlanActive = async (plan: SubscriptionPlan) => {
    try {
      const { error } = await supabase
        .from('subscription_plans')
        .update({ is_active: !plan.is_active })
        .eq('id', plan.id);

      if (error) throw error;

      toast({
        title: 'Sucesso',
        description: `Plano ${!plan.is_active ? 'ativado' : 'desativado'}`,
      });
      fetchPlans();
    } catch (error) {
      console.error('Error toggling plan:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível alterar o status do plano',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Planos</h1>
          <p className="text-muted-foreground">Gerencie os planos de assinatura</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Novo Plano
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPlan ? 'Editar Plano' : 'Criar Novo Plano'}</DialogTitle>
              <DialogDescription>
                {editingPlan ? 'Atualize as informações do plano' : 'Configure um novo plano de assinatura'}
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="id">ID do Plano</Label>
                <Input
                  id="id"
                  placeholder="ex: custom_plan"
                  value={formData.id}
                  onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                  disabled={!!editingPlan}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Nome</Label>
                <Input
                  id="name"
                  placeholder="Nome do plano"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Input
                  id="description"
                  placeholder="Descrição breve do plano"
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              
              <div className="col-span-2">
                <h4 className="font-medium mb-3">Limites</h4>
                <div className="grid grid-cols-4 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="max_inboxes">Inboxes</Label>
                    <Input
                      id="max_inboxes"
                      type="number"
                      value={formData.max_inboxes}
                      onChange={(e) => setFormData({ ...formData, max_inboxes: parseInt(e.target.value) || 0 })}
                    />
                    <p className="text-xs text-muted-foreground">-1 = ilimitado</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="max_agents">Agentes</Label>
                    <Input
                      id="max_agents"
                      type="number"
                      value={formData.max_agents}
                      onChange={(e) => setFormData({ ...formData, max_agents: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="max_channels">Canais</Label>
                    <Input
                      id="max_channels"
                      type="number"
                      value={formData.max_channels}
                      onChange={(e) => setFormData({ ...formData, max_channels: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="max_messages">Msgs/Mês</Label>
                    <Input
                      id="max_messages"
                      type="number"
                      value={formData.max_messages_month ?? ''}
                      onChange={(e) => setFormData({ ...formData, max_messages_month: e.target.value ? parseInt(e.target.value) : null })}
                      placeholder="NULL = ∞"
                    />
                  </div>
                </div>

                {/* Subseção: Módulo de Tarefas */}
                <h4 className="font-medium mb-3 mt-4">Módulo de Tarefas</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="max_tasks_month">Tarefas/Mês</Label>
                    <Input
                      id="max_tasks_month"
                      type="number"
                      value={formData.max_tasks_month}
                      onChange={(e) => setFormData({ ...formData, max_tasks_month: parseInt(e.target.value) || 0 })}
                    />
                    <p className="text-xs text-muted-foreground">-1 = ilimitado | Padrão: 50</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="max_tasks_storage_mb">Storage Tarefas (MB)</Label>
                    <Input
                      id="max_tasks_storage_mb"
                      type="number"
                      value={formData.max_tasks_storage_mb}
                      onChange={(e) => setFormData({ ...formData, max_tasks_storage_mb: parseInt(e.target.value) || 0 })}
                    />
                    <p className="text-xs text-muted-foreground">Padrão: 1024 (1 GB)</p>
                  </div>
                </div>
              </div>

              <div className="col-span-2">
                <h4 className="font-medium mb-3">Preços</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="price_monthly">Mensal</Label>
                    <Input
                      id="price_monthly"
                      type="text"
                      placeholder="R$ 0,00"
                      value={formData.price_monthly > 0 ? applyCurrencyMask(formData.price_monthly) : ''}
                      onChange={(e) => setFormData({ ...formData, price_monthly: parseCurrency(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="price_yearly">Anual</Label>
                    <Input
                      id="price_yearly"
                      type="text"
                      placeholder="R$ 0,00"
                      value={formData.price_yearly > 0 ? applyCurrencyMask(formData.price_yearly) : ''}
                      onChange={(e) => setFormData({ ...formData, price_yearly: parseCurrency(e.target.value) })}
                    />
                  </div>
                </div>
              </div>

              <div className="col-span-2 space-y-2">
                <Label htmlFor="features">Features (uma por linha)</Label>
                <Textarea
                  id="features"
                  placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
                  value={formData.features}
                  onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                  rows={4}
                />
              </div>

              <div className="col-span-2">
                <h4 className="font-medium mb-3">Stripe (Integração Futura)</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="stripe_product">Product ID</Label>
                    <Input
                      id="stripe_product"
                      placeholder="prod_..."
                      value={formData.stripe_product_id || ''}
                      onChange={(e) => setFormData({ ...formData, stripe_product_id: e.target.value || null })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="stripe_price_monthly">Price Mensal ID</Label>
                    <Input
                      id="stripe_price_monthly"
                      placeholder="price_..."
                      value={formData.stripe_price_id_monthly || ''}
                      onChange={(e) => setFormData({ ...formData, stripe_price_id_monthly: e.target.value || null })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="stripe_price_yearly">Price Anual ID</Label>
                    <Input
                      id="stripe_price_yearly"
                      placeholder="price_..."
                      value={formData.stripe_price_id_yearly || ''}
                      onChange={(e) => setFormData({ ...formData, stripe_price_id_yearly: e.target.value || null })}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="sort_order">Ordem de Exibição</Label>
                <Input
                  id="sort_order"
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="is_active">Plano Ativo</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit}>
                {editingPlan ? 'Salvar' : 'Criar Plano'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Planos de Assinatura
          </CardTitle>
          <CardDescription>
            {plans.length} planos configurados
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground">Carregando...</p>
          ) : plans.length === 0 ? (
            <p className="text-muted-foreground">Nenhum plano cadastrado</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plano</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead className="text-center">Inboxes</TableHead>
                  <TableHead className="text-center">Agentes</TableHead>
                  <TableHead className="text-center">Canais</TableHead>
                  <TableHead className="text-center">Msgs/Mês</TableHead>
                  <TableHead className="text-right">Preço Mensal</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.map((plan) => (
                  <TableRow key={plan.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{plan.name}</p>
                        <p className="text-xs text-muted-foreground">{plan.id}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground max-w-[200px] truncate">
                      {plan.description || '-'}
                    </TableCell>
                    <TableCell className="text-center">{formatLimit(plan.max_inboxes)}</TableCell>
                    <TableCell className="text-center">{formatLimit(plan.max_agents)}</TableCell>
                    <TableCell className="text-center">{formatLimit(plan.max_channels)}</TableCell>
                    <TableCell className="text-center">{formatLimit(plan.max_messages_month)}</TableCell>
                    <TableCell className="text-right font-medium">
                      {formatPrice(plan.price_monthly)}
                    </TableCell>
                    <TableCell className="text-center">
                      {plan.is_active ? (
                        <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
                          <Check className="h-3 w-3 mr-1" />
                          Ativo
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground">
                          <X className="h-3 w-3 mr-1" />
                          Inativo
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(plan)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => togglePlanActive(plan)}
                        >
                          {plan.is_active ? (
                            <X className="h-4 w-4 text-destructive" />
                          ) : (
                            <Check className="h-4 w-4 text-green-600" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPlans;
